import { AlertTriangle } from "lucide-react";
import { useState } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaOtpInput } from "@/shared/components/forms/ColocaOtpInput";
import { ColocaDialog } from "@/shared/components/overlays/ColocaDialog";
import { useTranslations } from "@/shared/lang/useTranslations";
import { HttpApiService } from "@/shared/services/httpApi.service";
import { readApiMessages } from "@/shared/utils/apiError";

import { settingsService } from "../services/settingsService";

interface Disable2FAModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export const Disable2FAModal = ({ isOpen, onClose, onSuccess }: Disable2FAModalProps) => {
  const { t } = useTranslations("settings");
  const [code, setCode] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleDisable = async (value = code) => {
    if (value.length !== 6 || isLoading) return;

    setIsLoading(true);
    setError(null);
    try {
      await settingsService.disable2FA(value);
      onSuccess();
      handleClose();
    } catch (err) {
      if (HttpApiService.isAxiosError(err)) {
        // `data.message` is a string[] whenever the ValidationPipe rejected the body, so reading it raw put
        // an array into a `string | null` state and React rendered the sentences run together.
        const message = readApiMessages(err).join(" ");
        setError(message || t("error_generic"));
      } else {
        setError(t("error_generic"));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setCode("");
    setError(null);
    onClose();
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) handleClose();
  };

  return (
    <ColocaDialog
      open={isOpen}
      onOpenChange={handleOpenChange}
      title={t("disable_twofa_title")}
      size="default"
      footer={
        <>
          <ColocaButton variant="ghost" onClick={handleClose} disabled={isLoading}>
            {t("disable_twofa_cancel_button")}
          </ColocaButton>
          <ColocaButton
            variant="destructive"
            onClick={() => void handleDisable()}
            loading={isLoading}
            disabled={code.length !== 6 || isLoading}
          >
            {t("disable_twofa_verify_button")}
          </ColocaButton>
        </>
      }
    >
      <div className="space-y-6">
        <ColocaAlert variant="warning" icon={AlertTriangle} text={t("disable_twofa_warning")} />

        <p className="text-sm text-muted-foreground">{t("disable_twofa_description")}</p>

        <div className="flex flex-col items-center gap-4">
          <ColocaOtpInput
            value={code}
            onValueChange={setCode}
            onComplete={(value) => void handleDisable(value)}
            disabled={isLoading}
            invalid={!!error}
            aria-label={t("disable_twofa_title")}
            autoFocus
          />

          {error && <p className="text-sm text-destructive">{error}</p>}
        </div>
      </div>
    </ColocaDialog>
  );
};
